import { Button, Drawer, Flex, Text } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { useEffect, useState, useRef } from "react";
import { FaCookieBite } from "react-icons/fa";
import { Link, useLocation } from "react-router-dom";
import { client_selected } from "../../client";
import Clarity from "@microsoft/clarity";
import {
  initGA,
  trackPageView,
} from "../../utils/AnalyticsGoogle/AnalyticsInit";
import { useTranslation } from "react-i18next";
import { usePrivacyPolicyByLanguage } from "../../utils/GetTerms";

declare global {
  interface Window {
    clarity?: {
      consent: (allow: boolean) => void;
    };
  }
}

export default function Cookies() {
  const { t } = useTranslation();
  const location = useLocation();
  const [opened, { close, open }] = useDisclosure(false);

  const privacyPolicy = usePrivacyPolicyByLanguage();
  const idClarity = process.env.REACT_APP_CLARITY_ID;

  const [cookieConsent, setCookieConsent] = useState({
    cookiesChoiceEssential:
      localStorage.getItem("cookiesChoiceEssential") === "accepted",
    cookiesChoiceClarity:
      localStorage.getItem("cookiesChoiceClarity") === "accepted",
    cookiesChoiceAnalytics:
      localStorage.getItem("cookiesChoiceAnalytics") === "accepted",
    cookiesChoiceIP: localStorage.getItem("cookiesChoiceIP") === "accepted",
  });

  const gaInitialized = useRef(false);
  const clarityInitialized = useRef(false);

  const updateConsentState = () => {
    const essentialConsent = localStorage.getItem("cookiesChoiceEssential");
    const clarityConsent = localStorage.getItem("cookiesChoiceClarity");
    const GAConsent = localStorage.getItem("cookiesChoiceAnalytics");
    const IPConsent = localStorage.getItem("cookiesChoiceIP");

    setCookieConsent({
      cookiesChoiceEssential: essentialConsent === "accepted",
      cookiesChoiceClarity: clarityConsent === "accepted",
      cookiesChoiceAnalytics: GAConsent === "accepted",
      cookiesChoiceIP: IPConsent === "accepted",
    });
  };

  useEffect(() => {
    updateConsentState();
  }, []);

  useEffect(() => {
    if (
      idClarity &&
      cookieConsent.cookiesChoiceClarity &&
      !clarityInitialized.current
    ) {
      Clarity.init(idClarity);
      clarityInitialized.current = true;
      setTimeout(() => {
        Clarity.consent(true);
      }, 500);
    }

    if (cookieConsent.cookiesChoiceAnalytics && !gaInitialized.current) {
      initGA();
      gaInitialized.current = true;
      trackPageView(location.pathname + location.search);
    }
  }, [idClarity, cookieConsent, location.pathname, location.search]);

  // Checa consentimento ao montar
  useEffect(() => {
    if (!cookieConsent.cookiesChoiceEssential) {
      open();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cookieConsent.cookiesChoiceEssential]);

  useEffect(() => {
    if (cookieConsent.cookiesChoiceAnalytics && gaInitialized.current) {
      trackPageView(location.pathname + location.search);
    }
  }, [location, cookieConsent.cookiesChoiceAnalytics]);

  useEffect(() => {
    const onStorage = () => {
      updateConsentState();
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const handleAccept = () => {
    localStorage.setItem("cookiesChoiceEssential", "accepted");
    localStorage.setItem("cookiesChoiceClarity", "accepted");
    localStorage.setItem("cookiesChoiceAnalytics", "accepted");
    localStorage.setItem("cookiesChoiceIP", "accepted");

    updateConsentState();

    if (idClarity && !clarityInitialized.current) {
      Clarity.init(idClarity);
      clarityInitialized.current = true;
      setTimeout(() => {
        Clarity.consent(true);
      }, 500);
    } else if (window.clarity) {
      try {
        Clarity.consent(true);
      } catch (error) {
        console.error("❌ [Clarity] Erro ao aprovar consentimento:", error);
      }
    }

    if (!gaInitialized.current) {
      initGA();
      gaInitialized.current = true;
      trackPageView(location.pathname + location.search);
    }

    close();
  };

  const handleDecline = () => {
    localStorage.setItem("cookiesChoiceEssential", "accepted");
    localStorage.setItem("cookiesChoiceClarity", "declined");
    localStorage.setItem("cookiesChoiceAnalytics", "declined");
    localStorage.setItem("cookiesChoiceIP", "declined");

    updateConsentState();

    if (window.clarity) {
      try {
        Clarity.consent(false);
      } catch (error) {
        console.error("❌ [Clarity] Erro ao negar consentimento:", error);
      }
    }

    close();
  };

  return (
    <Drawer
      position="bottom"
      opened={opened}
      onClose={close}
      withCloseButton={false}
      closeOnClickOutside={false}
      closeOnEscape={false}
      size={""}
      overlayProps={{ opacity: 0.2 }}
      styles={{
        body: {
          padding: "0px",
          borderTopLeftRadius: "12px",
          borderTopRightRadius: "12px",
          overflow: "hidden",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexDirection: "column",
          height: "fit-content",
        },
        content: {
          maxHeight: "fit-content !important",
          padding: "0px  !important",
          borderTopLeftRadius: "20px !important",
          borderTopRightRadius: "20px  !important",
          overflow: "hidden",
        },
      }}
    >
      <Flex
        justify="center"
        align="center"
        w="100%"
        bg="primaryOpacity.2"
        style={{ borderBottom: "1px solid #B2B1B1" }}
      >
        <Flex
          maw="1920px"
          w="100%"
          p={{ base: "16px", 991: "16px 50px" }}
          gap="16px"
          justify="start"
          align="center"
        >
          <FaCookieBite size={36} color="black" />
          <Text fz="24px" weight={500}>
            {t("global.cookies.title")}
          </Text>
        </Flex>
      </Flex>
      <Flex
        align={"center"}
        justify={"center"}
        w="100%"
        p={{ base: "24px", 991: "40px" }}
        gap={{ base: "16px", 991: "32px" }}
        direction={{ base: "column", 991: "row" }}
      >
        <Flex w="100%" style={{ flex: "1" }}>
          <Text
            w="100%"
            c="basic.4"
            fz={{ base: "14px", 991: "16px" }}
            lh="32px"
            ta="center"
            style={{ whiteSpace: "pre-wrap" }}
          >
            {t("global.cookies.description")}{" "}
            <Link to={privacyPolicy} target="_blank">
              {t("global.cookies.link")}
            </Link>
            .
          </Text>
        </Flex>
        <Flex
          gap="16px"
          w="100%"
          justify="center"
          wrap={"wrap"}
          style={{ flex: "1" }}
        >
          <Button
            variant="primary"
            onClick={handleAccept}
            w="100%"
            miw="320px"
            style={{ flex: "1" }}
          >
            {t("global.cookies.button_accept")}
          </Button>
          <Button
            variant="primaryOutline"
            onClick={handleDecline}
            w="100%"
            miw="320px"
            style={{ flex: "1" }}
          >
            {t("global.cookies.button_reject")}
          </Button>
        </Flex>
      </Flex>
    </Drawer>
  );
}
